import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  defaultRequestOptions,
  HttpDataAccess,
  SseClientSubscriber,
  SseOptions,
} from '@softline/core';
import { map, Observable } from 'rxjs';
import {
  AI_AGENT_API_COPILOTKIT,
  AI_AGENT_API_FEEDBACK,
  AI_AGENT_API_HEALTH,
} from '../ai-agent.api';
import {
  DEFAULT_AI_AGENT_CONFIG,
  SOFTLINE_CONFIG_AI_AGENT,
} from '../ai-agent.shared';
import {
  AgUiEvent,
  AgUiRunEnvelope,
  FeedbackInput,
  RunAgentInput,
} from '../types/ag-ui.types';

/**
 * One-shot POST per run, never reconnecting: `keepAlive: false` is load-bearing,
 * because a reconnect would re-POST the envelope and run the agent a second time.
 */
const RUN_SSE_OPTIONS: SseOptions = {
  keepAlive: false,
  reconnectionDelay: 0,
  responseType: 'event',
};

/**
 * Data access for the ai-agent service. Built on HttpClient rather than
 * ApiDataAccess — the agent is not the Softline connection and its base URL comes
 * from the module config. The URL stays relative so the SoftApps JWT the global
 * interceptor attaches never leaves this origin.
 */
@Injectable()
export class AiAgentRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly dataAccess = inject(HttpDataAccess);
  private readonly config = inject(SOFTLINE_CONFIG_AI_AGENT);

  private get baseUrl(): string {
    return this.config.apiBaseUrl ?? DEFAULT_AI_AGENT_CONFIG.apiBaseUrl;
  }

  get agentId(): string {
    return this.config.agentId ?? DEFAULT_AI_AGENT_CONFIG.agentId;
  }

  /**
   * Streams one `agent/run`. Emits parsed AG-UI events; an ErrorEvent means the
   * transport failed — the subscriber completes without erroring on status 0.
   */
  run(input: RunAgentInput): Observable<AgUiEvent | ErrorEvent> {
    const envelope: AgUiRunEnvelope = {
      method: 'agent/run',
      params: { agentId: this.agentId },
      body: input,
    };

    return new SseClientSubscriber(
      this.httpClient,
      RUN_SSE_OPTIONS,
      {
        ...defaultRequestOptions,
        body: envelope,
        headers: { 'Content-Type': 'application/json' },
      },
      this.baseUrl + AI_AGENT_API_COPILOTKIT,
      'POST'
    )
      .createObservable()
      .pipe(map((event) => this.parseEvent(event)));
  }

  /** 503 while Langfuse is unconfigured — the caller reports, it is not a crash. */
  async sendFeedback(input: FeedbackInput): Promise<void> {
    await this.dataAccess.create({ url: this.baseUrl + AI_AGENT_API_FEEDBACK }, input);
  }

  async isHealthy(): Promise<boolean> {
    try {
      await this.dataAccess.get({ url: this.baseUrl + AI_AGENT_API_HEALTH });
      return true;
    } catch {
      return false;
    }
  }

  private parseEvent(event: string | Event): AgUiEvent | ErrorEvent {
    if (event instanceof ErrorEvent) return event;
    const data = (event as MessageEvent).data;
    if (typeof data !== 'string' || data.length === 0)
      return { type: 'UNKNOWN' } as AgUiEvent;
    try {
      return JSON.parse(data) as AgUiEvent;
    } catch {
      // A truncated frame is not worth failing the run over; the stream continues.
      return { type: 'UNPARSEABLE', raw: data } as AgUiEvent;
    }
  }
}
